/**
 * useEmailCorrelationEvidenceData
 *
 * DASH-EMAIL-011: real-data hook for the Overview view's "Correlation
 * Evidence" presentation -- an independent fetch against the real,
 * read-only MESSAGE-grain correlation-evidence Dashboard API
 * (emailDeliveryCorrelationEvidenceApi.js -> GET /api/email-delivery/
 * dashboard/correlation-evidence), mirroring useEmailRecipientDndData.js's
 * own independent-fetch pattern: this is a MESSAGE-grain read, strictly
 * separate from any RECIPIENT-grain (hygiene/DND) or CHECKPOINT-grain
 * (Processing Health) surface elsewhere on the Dashboard, so a failure
 * here must never block any other section from rendering, and a failure
 * elsewhere must never affect this hook's own result either.
 *
 * Never fabricates a correlation count. On:
 *   - a request failure (network/5xx/etc),
 *   - a malformed/unrecognized response shape (evidence_status is neither
 *     "available" nor "unavailable"),
 *   - or the backend's own evidence_status === "unavailable"
 *       (no GHL email discovery/tracking evidence has been collected for
 *        this tenant at all -- NOT "0 correlated messages"),
 * every count is surfaced as `null`. A real, backend-confirmed zero for
 * one bucket (evidence_status === "available") is preserved as an honest
 * 0, never coerced to null and never confused with the unavailable case.
 */

import { useState, useEffect, useCallback } from "react";
import { getEmailDeliveryCorrelationEvidence } from "../lib/emailDeliveryCorrelationEvidenceApi";

function numberOrNull(value) {
  return typeof value === "number" ? value : null;
}

export function useEmailCorrelationEvidenceData() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [correlatedMessageCount, setCorrelatedMessageCount] = useState(null);
  const [awaitingCorrelationMessageCount, setAwaitingCorrelationMessageCount] = useState(null);
  const [evidenceStatus, setEvidenceStatus] = useState(null);

  const fetchEvidence = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getEmailDeliveryCorrelationEvidence();
      const status = response?.evidence_status;

      if (status !== "available" && status !== "unavailable") {
        // Malformed/unrecognized response shape -- never fabricate a
        // correlation count from a shape this contract doesn't recognize.
        setEvidenceStatus("unavailable");
        setCorrelatedMessageCount(null);
        setAwaitingCorrelationMessageCount(null);
        return;
      }

      setEvidenceStatus(status);
      if (status === "unavailable") {
        setCorrelatedMessageCount(null);
        setAwaitingCorrelationMessageCount(null);
      } else {
        setCorrelatedMessageCount(numberOrNull(response.correlated_message_count));
        setAwaitingCorrelationMessageCount(numberOrNull(response.awaiting_correlation_message_count));
      }
    } catch (err) {
      // Defensive, independent of every other Dashboard fetch -- never
      // blocks the rest of the view from rendering, never fabricates a
      // correlation count on failure.
      setError(err?.message || "Failed to load Email correlation evidence");
      setEvidenceStatus(null);
      setCorrelatedMessageCount(null);
      setAwaitingCorrelationMessageCount(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvidence();
  }, [fetchEvidence]);

  return {
    loading,
    error,
    correlatedMessageCount,
    awaitingCorrelationMessageCount,
    evidenceStatus,
    refresh: fetchEvidence,
  };
}

export default useEmailCorrelationEvidenceData;
